import { useModalDialog } from "../hooks/useModalDialog";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, incrementQty, decrementQty } from "../redux/cartSlice";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/apiConfig";
import Navbar from "../components/Navbar";
import HlcFooter from "../components/HlcFooter";
import { cssVars } from "../theme";
import { useProductFilters } from "../hooks/useProductFilters";
import ProductCard from "../components/shop/ProductCard";
import QuickViewModal from "../components/shop/QuickViewModal";
import { TagIcon, FilterIcon, ChevronIcon, CloseIcon } from "../components/shop/ShopIcons";
import "../styles/hlcShopSystem.css";

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A-Z" },
];

const DiscountedProducts = () => {
  const [discountedProducts, setDiscountedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("featured");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { dialogRef, isModal } = useModalDialog(mobileFiltersOpen, () => setMobileFiltersOpen(false), "(max-width: 991px)");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const getCartItem = (id) => cartItems.find((item) => item.id === id);
  const navigate = useNavigate();

  // Fetch discounted products from adminhlc-api (MySQL) — unchanged from
  // the original implementation, same endpoint/filter predicate.
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const filtered = Array.isArray(data)
          ? data.filter((p) => p.discount && p.discountedPrice && p.discount !== "0%")
          : [];
        setDiscountedProducts(filtered);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch discounted products:", err);
        if (!cancelled) {
          setDiscountedProducts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Same cascading Category → Product → Watt/Model filter engine used on
  // /shop, applied over the discounted-only subset.
  const {
    filteredProducts,
    filterMainCategory,
    filterProduct,
    filterWatt,
    mainList,
    productsList,
    wattList,
    handleMainChange,
    handleProductChange,
    handleWattChange,
    applyFilters,
    clearFilters,
    activeFilterCount,
  } = useProductFilters(discountedProducts);

  const sortedProducts = useMemo(() => {
    const arr = [...filteredProducts];
    const priceOf = (p) => Number(p.discountedPrice || p.price) || 0;
    switch (sortBy) {
      case "price-asc": return arr.sort((a, b) => priceOf(a) - priceOf(b));
      case "price-desc": return arr.sort((a, b) => priceOf(b) - priceOf(a));
      case "name-asc": return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      default: return arr;
    }
  }, [filteredProducts, sortBy]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredProducts, sortBy]);

  const visibleProducts = sortedProducts.slice(0, visibleCount);
  const hasMore = visibleCount < sortedProducts.length;

  const handleApplyFilters = () => {
    applyFilters();
    setMobileFiltersOpen(false);
  };

  const viewProduct = (id) => navigate(`/product/${id}`);
  const openModal = (product) => { setSelectedProduct(product); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setSelectedProduct(null); };

  const filterPanel = (
    <>
      <div className="hlc-shop__filters-head">
        <h3>
          Filters
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </h3>
        <button type="button" className="hlc-shop__filters-close" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters">
          <CloseIcon />
        </button>
      </div>

      <label htmlFor="disc_mainCategory">Category</label>
      <div className="hlc-shop__select-wrap">
        <select id="disc_mainCategory" value={filterMainCategory} onChange={handleMainChange} className={filterMainCategory ? "has-value" : ""}>
          <option value="">All Categories</option>
          {mainList.map((main) => <option key={main} value={main}>{main}</option>)}
        </select>
        <ChevronIcon />
      </div>

      {filterMainCategory && (
        <>
          <label htmlFor="disc_product">Product</label>
          <div className="hlc-shop__select-wrap">
            <select id="disc_product" value={filterProduct} onChange={handleProductChange} className={filterProduct ? "has-value" : ""}>
              <option value="">All Products</option>
              {productsList.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <ChevronIcon />
          </div>
        </>
      )}

      {filterProduct && (
        <>
          <label htmlFor="disc_watt">Watt / Model</label>
          <div className="hlc-shop__select-wrap">
            <select id="disc_watt" value={filterWatt} onChange={handleWattChange} className={filterWatt ? "has-value" : ""}>
              <option value="">All Watts / Models</option>
              {wattList.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
            <ChevronIcon />
          </div>
        </>
      )}

      <div className="filter-actions">
        <button type="button" id="applyFilters" onClick={handleApplyFilters}>Apply Filters</button>
        <button type="button" id="clearFilters" onClick={clearFilters}>Clear All</button>
      </div>
    </>
  );

  return (
    <>
      <Navbar />
      <div style={{ marginTop: "var(--nav-h, 74px)" }} />

      <div id="hlc-discount-page" className="hlc-shop-system" style={cssVars}>
        <section className="hlc-page-hero">
          <div className="hlc-page-hero-inner">
            <span className="hlc-page-hero-icon"><TagIcon /></span>
            <div>
              <span className="hlc-page-hero-eyebrow">Special Offers</span>
              <h1>Lighting upgrades, better prices.</h1>
              <p>Explore selected HLC lighting products at exceptional prices.</p>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="hlc-shop__loading" style={{ padding: "60px 20px" }}>Loading offers…</div>
        ) : discountedProducts.length === 0 ? (
          <div className="hlc-empty-panel">
            <span className="hlc-empty-panel-icon"><TagIcon /></span>
            <h2>No Active Offers</h2>
            <p>There are no discounted products available right now. Explore our complete lighting collection instead.</p>
            <button type="button" onClick={() => navigate("/shop")}>View All Products →</button>
          </div>
        ) : (
          <>
            <div className="hlc-shop__toolbar">
              <p className="hlc-shop__count">
                Showing <b>{visibleProducts.length}</b> of <b>{sortedProducts.length}</b> deal
                {sortedProducts.length !== 1 ? "s" : ""}
              </p>

              <div className="hlc-shop__toolbar-right">
                <button type="button" className="hlc-shop__mobile-filters-btn" aria-expanded={mobileFiltersOpen} aria-controls="hlc-product-filters" onClick={() => setMobileFiltersOpen(true)}>
                  <FilterIcon /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </button>

                <div className="hlc-shop__sort">
                  <label htmlFor="disc_sort">Sort by</label>
                  <div className="hlc-shop__sort-wrap">
                    <select id="disc_sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <ChevronIcon />
                  </div>
                </div>
              </div>
            </div>

            <div className="hlc-shop__inner">
              <div className={`hlc-shop__filters-scrim${mobileFiltersOpen ? " is-open" : ""}`} onClick={() => setMobileFiltersOpen(false)} />
              <aside id="hlc-product-filters" ref={dialogRef} role={isModal && mobileFiltersOpen ? "dialog" : undefined} aria-hidden={isModal && !mobileFiltersOpen ? true : undefined} aria-modal={isModal && mobileFiltersOpen ? true : undefined} aria-label="Product filters" tabIndex={-1} inert={isModal && !mobileFiltersOpen ? true : undefined} className={`hlc-shop__filters${mobileFiltersOpen ? " is-open" : ""}`}>
                {filterPanel}
              </aside>

              <main>
                <div className="hlc-shop__grid">
                  {visibleProducts.length === 0 ? (
                    <div className="hlc-shop__empty">No products found. Try clearing filters.</div>
                  ) : (
                    visibleProducts.map((product, idx) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        idx={idx}
                        cartItem={getCartItem(product.id)}
                        onAddToCart={(p) => dispatch(addToCart(p))}
                        onIncrement={(id) => dispatch(incrementQty(id))}
                        onDecrement={(id) => dispatch(decrementQty(id))}
                        onQuickView={openModal}
                        onView={viewProduct}
                      />
                    ))
                  )}
                </div>

                {hasMore && (
                  <div className="hlc-shop__loadmore">
                    <button type="button" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                      Load More Deals
                    </button>
                  </div>
                )}
              </main>
            </div>
          </>
        )}
      </div>

      <HlcFooter />

      <QuickViewModal
        product={selectedProduct}
        open={modalOpen}
        onClose={closeModal}
        cartItem={selectedProduct ? getCartItem(selectedProduct.id) : null}
        onAddToCart={(p) => dispatch(addToCart(p))}
        onIncrement={(id) => dispatch(incrementQty(id))}
        onDecrement={(id) => dispatch(decrementQty(id))}
        onViewFull={viewProduct}
      />
    </>
  );
};

export default DiscountedProducts;
